import styled from '@emotion/styled';
import { Col, Row } from 'antd';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    fetchSeveralProgramSurveys,
    submitSeveralProgramSurveys,
} from 'src/modules/programs/surveys/actions/surveysActions';
import { SurveyHeader } from 'src/modules/programs/surveys/components/styles';
import { AnswersBySurveysIds } from 'src/modules/programs/surveys/models/surveys';
import {
    areCertainProgramSurveysAnswered,
    getSurveysAnswersBySurveysIds,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { CBTProgramWeekSevenSurvey } from '../../weekSeven/models/weekSeven';
import { ExposureCompletionStatus } from '../components/ExposureCompletionStatus';
import { CBTProgramWeekEightSurvey } from '../models/weekEight';

const ExposuresHeader = styled(Row)({
    position: 'relative',
    alignItems: 'center',
    paddingBottom: '1rem',
    marginBottom: '1rem',
    borderBottom: `2px solid ${color.mariner}`,
});

const StatusLabels = styled(Col)({
    display: 'flex',
    position: 'absolute',
    top: 0,
    right: 0,
    alignItems: 'center',
    [mediaQueries.sm]: {
        display: 'none',
    },
});

const StatusLabel = styled.span(
    {
        display: 'block',
        width: '50%',
        textAlign: 'center',
        color: color.baliHai,
    },
    font.medium(font.size.base),
);

const exposuresSurveysIds = [
    CBTProgramWeekSevenSurvey.PickAnExposure1,
    CBTProgramWeekSevenSurvey.PickAnExposure2,
];

const exposuresCompletionSurveysIds = [
    CBTProgramWeekEightSurvey.ExposureCompletion1,
    CBTProgramWeekEightSurvey.ExposureCompletion2,
];

interface StateProps {
    exposuresAnswers: AnswersBySurveysIds | undefined;
    answers: AnswersBySurveysIds | undefined;
    areSurveysAnswered: boolean;
}

interface ActionsProps {
    fetchSeveralProgramSurveys: typeof fetchSeveralProgramSurveys;
    submitSeveralProgramSurveys: typeof submitSeveralProgramSurveys;
}

interface Props extends StateProps, WithTranslation, ActionsProps {
    goToPreviousStep: () => void;
    goToNextStep: () => void;
    programId: ProgramEnum;
}

class ExposuresCompletionStepContainer extends React.Component<Props> {
    public componentDidMount(): void {
        const { programId } = this.props;

        this.props.fetchSeveralProgramSurveys({
            programId,
            surveysIds: [...exposuresSurveysIds, ...exposuresCompletionSurveysIds],
        });
    }

    private handleSubmit = () => {
        const { goToNextStep, programId, answers } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitSeveralProgramSurveys({
            programId,
            answers,
        });

        goToNextStep();
    };

    public render(): JSX.Element | null {
        const { t, exposuresAnswers, programId, goToPreviousStep, areSurveysAnswered } = this.props;

        const completionLabels: string[] = t('common.completionLabels', { returnObjects: true });

        return exposuresAnswers ? (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>{t('cbt:week-8.check-in.title')}</S.StepHeading>
                        <S.StepBoxWrapper boxShadow style={{ padding: '2rem' }}>
                            <ExposuresHeader type="flex">
                                <SurveyHeader
                                    style={{ marginBottom: 0, paddingBottom: 0, border: 'none' }}
                                >
                                    {t('cbt:week-8.check-in.exposuresHeading')}
                                </SurveyHeader>
                                <StatusLabels sm={6}>
                                    {completionLabels.map(label => (
                                        <StatusLabel key={label}>{label}</StatusLabel>
                                    ))}
                                </StatusLabels>
                            </ExposuresHeader>
                            {Object.values(exposuresCompletionSurveysIds).map(
                                (surveyId, index: number) => (
                                    <ExposureCompletionStatus
                                        key={surveyId}
                                        programId={programId}
                                        surveyId={surveyId}
                                        exposureAnswers={
                                            exposuresAnswers[exposuresSurveysIds[index]]
                                        }
                                    />
                                ),
                            )}
                        </S.StepBoxWrapper>
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!areSurveysAnswered}
                    hasNextStep
                />
            </>
        ) : (
            <CenteredSpinner />
        );
    }
}

const mapStateToProps = (state: AppState, { programId }: Props): StateProps => ({
    exposuresAnswers: getSurveysAnswersBySurveysIds(state, programId, exposuresSurveysIds),
    answers: getSurveysAnswersBySurveysIds(state, programId, exposuresCompletionSurveysIds),
    areSurveysAnswered: areCertainProgramSurveysAnswered(
        state,
        programId,
        exposuresCompletionSurveysIds,
    ),
});

export const ExposuresCompletionStep = withTranslation()(
    connect(mapStateToProps, { fetchSeveralProgramSurveys, submitSeveralProgramSurveys })(
        ExposuresCompletionStepContainer,
    ),
);
